import { useContext, useState } from 'react';
import { ChevronRightIcon, HomeIcon, MoreHorizontalIcon } from 'lucide-react';
import clsx from 'clsx';
import { EntityRecord } from '../../../../api/api-entities';
import { useBreadcrumbs } from '../../../../hooks/use-breadcrumbs';
import { AuthContext } from '@@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@@/ui/components';

export function Breadcrumbs(props: { currentEntity?: EntityRecord }) {
  const { getPathWithInstanceAlias } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const { breadcrumbs, isLoading: breadcrumbsLoading } = useBreadcrumbs({
    currentEntityId: props.currentEntity ? props.currentEntity.entityId : 'root'
  });
  const shouldShowCollapsed = breadcrumbs.length > 3 && !isExpanded;
  const visibleBreadcrumbs = shouldShowCollapsed
    ? [breadcrumbs[0], breadcrumbs[1], breadcrumbs[breadcrumbs.length - 1]]
    : breadcrumbs;

  const handleBreadcrumbNavigate = (targetParentId: string) => {
    navigate(getPathWithInstanceAlias(`/portal/dfc/browser/${targetParentId}`));
  };

  const renderBreadcrumbItem = (bc: (typeof breadcrumbs)[0], index: number) => (
    <div
      key={index}
      className={clsx(
        'flex items-center gap-1 py-1',
        'rounded-md cursor-pointer',
        'text-sm',
        'text-nowrap',
        breadcrumbs.length - 1 === index ? 'font-semibold text-slate-950' : 'hover:text-slate-900 text-slate-500'
      )}
      onClick={() => handleBreadcrumbNavigate(bc.parentId)}
    >
      {!bc.isLoading && bc.parentId === 'root' && !bc.name && (
        <>
          <HomeIcon className='size-4' />
        </>
      )}
      {!bc.isLoading && bc.parentId !== 'root' && (
        <>
          <ChevronRightIcon className='size-4' />
          <span className='font-medium'>{bc.name}</span>
        </>
      )}
    </div>
  );

  const renderExpandButton = () => (
    <div
      className={clsx(
        'flex items-center gap-1 py-1',
        'rounded-md cursor-pointer',
        'text-sm text-slate-500 hover:text-slate-900'
      )}
      onClick={() => setIsExpanded(true)}
    >
      <ChevronRightIcon className='size-4' />
      <MoreHorizontalIcon className='size-4' />
    </div>
  );

  return (
    <div className={clsx('flex items-center h-10 px-3 justify-between', 'bg-slate-50 border-b border-slate-300')}>
      <div className='flex items-center gap-1 overflow-scroll scrollbar-hide'>
        {breadcrumbsLoading && (
          <>
            <div
              className={clsx(
                'flex items-center gap-1 py-1 mr-2',
                'hover:text-slate-900 text-slate-500 cursor-pointer'
              )}
              onClick={() => handleBreadcrumbNavigate('root')}
            >
              <HomeIcon className='size-4' />
            </div>
            <Spinner size='sm' />
          </>
        )}
        {!breadcrumbsLoading && (
          <>
            {shouldShowCollapsed ? (
              <>
                {renderBreadcrumbItem(visibleBreadcrumbs[0], 0)}
                {renderBreadcrumbItem(visibleBreadcrumbs[1], 1)}
                {renderExpandButton()}
                {renderBreadcrumbItem(visibleBreadcrumbs[2], breadcrumbs.length - 1)}
              </>
            ) : (
              visibleBreadcrumbs.map((bc, index) => renderBreadcrumbItem(bc, index))
            )}
          </>
        )}
      </div>
    </div>
  );
}
